import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, Auth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, Firestore } from 'firebase/firestore';
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable,
  Functions,
} from 'firebase/functions';
import type {
  CreatePaymentLinkDraftInput,
  DeactivatePaymentLinkInput,
  DeletePaymentLinkDraftInput,
  DeletePaymentLinkDraftResult,
  SetPaymentLinkVisibilityInput,
  UpdatePaymentLinkDraftInput,
  UserActivityEvent,
  PaymentLink,
} from '@pay888/shared';
import { firebaseConfig } from './firebaseConfig.js';

export { isFirebaseConfigured } from './firebaseConfig.js';

const FIREBASE_FUNCTIONS_REGION = 'europe-west1';

function shouldUseEmulators(): boolean {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const isLocalhost =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isLocalhost) {
      // Live hosted environment (pay888.app, preview channel, web.app) -> always use real Firebase services
      return false;
    }
  }
  return import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';
}

export function getFirebaseApp(): FirebaseApp {
  if (getApps().length === 0) {
    return initializeApp(firebaseConfig);
  }
  return getApp();
}

export function getFirebaseAuth(): Auth {
  const app = getFirebaseApp();
  const auth = getAuth(app);

  if (shouldUseEmulators()) {
    try {
      connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    } catch (_err) {
      // Emulator connection already established
    }
  }

  return auth;
}

export function getFirebaseFirestore(): Firestore {
  const app = getFirebaseApp();
  const db = getFirestore(app);

  if (shouldUseEmulators()) {
    try {
      connectFirestoreEmulator(db, '127.0.0.1', 8080);
    } catch (_err) {
      // Emulator connection already established
    }
  }

  return db;
}

export function getFirebaseFunctions(): Functions {
  const app = getFirebaseApp();
  const functions = getFunctions(app, FIREBASE_FUNCTIONS_REGION);

  if (shouldUseEmulators()) {
    try {
      connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    } catch (_err) {
      // Emulator connection already established
    }
  }

  return functions;
}

export function getCreatePaymentLinkDraftCallable() {
  const functions = getFirebaseFunctions();
  return httpsCallable<CreatePaymentLinkDraftInput, PaymentLink>(
    functions,
    'createPaymentLinkDraft'
  );
}

export function getUpdatePaymentLinkDraftCallable() {
  const functions = getFirebaseFunctions();
  return httpsCallable<UpdatePaymentLinkDraftInput, PaymentLink>(
    functions,
    'updateMyPaymentLinkDraft'
  );
}

export function getDeletePaymentLinkDraftCallable() {
  const functions = getFirebaseFunctions();
  return httpsCallable<DeletePaymentLinkDraftInput, DeletePaymentLinkDraftResult>(
    functions,
    'deleteMyPaymentLinkDraft'
  );
}

export function getSetPaymentLinkVisibilityCallable() {
  const functions = getFirebaseFunctions();
  return httpsCallable<SetPaymentLinkVisibilityInput, PaymentLink>(
    functions,
    'setMyPaymentLinkDraftVisibility'
  );
}

export function getListMyPaymentLinkDraftsCallable() {
  const functions = getFirebaseFunctions();
  return httpsCallable<undefined, readonly PaymentLink[]>(functions, 'listMyPaymentLinkDrafts');
}

export function getListMyActivityEventsCallable() {
  const functions = getFirebaseFunctions();
  return httpsCallable<undefined, readonly UserActivityEvent[]>(functions, 'listMyActivityEvents');
}

export function getDeactivatePaymentLinkDraftCallable() {
  const functions = getFirebaseFunctions();
  return httpsCallable<DeactivatePaymentLinkInput, PaymentLink>(
    functions,
    'deactivateMyPaymentLinkDraft'
  );
}

export function getPublicPaymentLinkEndpoint(slug: string): string {
  const region = FIREBASE_FUNCTIONS_REGION;
  const projectId = firebaseConfig.projectId;

  if (shouldUseEmulators()) {
    return `http://127.0.0.1:5001/${projectId}/${region}/getPublicPaymentLinkDraft?slug=${encodeURIComponent(slug)}`;
  }

  return `https://${region}-${projectId}.cloudfunctions.net/getPublicPaymentLinkDraft?slug=${encodeURIComponent(slug)}`;
}
